import { type UseHotkeyDefinition, useHotkeys } from '@tanstack/react-hotkeys'
import { useLocation, useNavigate } from '@tanstack/react-router'
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { useGetUserSelf } from '@/hooks/v1'
import { useSettings } from '@/providers/SettingsProvider'
import { KEYBOARD_SHORTCUTS } from '@/types/shortcuts'
import { useApiKey } from '@/utils/apiKeyAuth'
import { useFirebaseAuth } from '@/utils/firebaseAuthProvider'

interface ShortcutContextProps {
  shortcut: string
  tradeKey: number
  orderBookKey: number
  shortcutKey: number
  reset: () => void
}

const ShortcutContext = createContext<ShortcutContextProps | undefined>(undefined)

const ShortcutProvider = ({ children }: PropsWithChildren) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { hotkeysEnabled } = useSettings()
  const user = useFirebaseAuth()
  const apiKey = useApiKey()
  const isAuthRoute = location.pathname.startsWith('/auth/') || location.pathname === '/login'
  const isPublicRoute = location.pathname === '/viewOnlyMode'
  const isSessionAuthenticated = Boolean(user) || Boolean(apiKey)
  const { data: userSelfData } = useGetUserSelf({
    query: {
      enabled: isSessionAuthenticated && !isAuthRoute && !isPublicRoute,
    },
  })
  const userSelf = useMemo(() => userSelfData?.data ?? null, [userSelfData])
  const userId = useMemo(() => userSelf?.id ?? null, [userSelf])
  const [shortcut, setShortcut] = useState('')
  const [tradeKey, setTradeKey] = useState(0)
  const [orderBookKey, setOrderBookKey] = useState(0)
  const [shortcutKey, setShortcutKey] = useState(0)
  const hotkeysEnabledForSession = hotkeysEnabled && Boolean(userId)

  const reset = () => {
    setTradeKey(0)
    setOrderBookKey(0)
    setShortcutKey(0)
    setShortcut('')
  }

  const isShortcutTargetDisabled = useCallback((target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return false

    if (
      target.hasAttribute('data-hotkeys-disabled') ||
      target.closest('[data-hotkeys-disabled]') !== null
    ) {
      return true
    }

    // Inputs opt in individually, while data-hotkeys-disabled on any ancestor always wins.
    const formControl = target.closest('input, textarea, select, [contenteditable]')

    return formControl !== null && !formControl.hasAttribute('data-hotkeys-enabled')
  }, [])

  const handleShortcut = useCallback(
    (
      event: KeyboardEvent,
      shortcut: string,
      path: string,
      increaseTradeKey = true,
      increaseOrderBookKey = false,
    ) => {
      if (
        isShortcutTargetDisabled(event.target) ||
        isShortcutTargetDisabled(document.activeElement)
      ) {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      setShortcut(shortcut)
      // Repeated presses of the same shortcut still need to trigger consumers.
      setShortcutKey((prev) => prev + 1)

      if (increaseTradeKey) setTradeKey((prev) => prev + 1)
      if (increaseOrderBookKey) setOrderBookKey((prev) => prev + 1)

      if (!window.location.pathname.includes('/trade')) {
        navigate({ to: path })
      }
    },
    [isShortcutTargetDisabled, navigate],
  )

  const shortcutDefinitions = useMemo<Array<UseHotkeyDefinition>>(
    () => [
      {
        hotkey: 'Shift+B',
        callback: (event) => handleShortcut(event, KEYBOARD_SHORTCUTS.MARKET_ORDER_BUY, '/trade'),
        options: { ignoreInputs: false },
      },
      {
        hotkey: 'Shift+S',
        callback: (event) => handleShortcut(event, KEYBOARD_SHORTCUTS.MARKET_ORDER_SELL, '/trade'),
        options: { ignoreInputs: false },
      },
      {
        hotkey: 'Shift+O',
        callback: (event) => handleShortcut(event, KEYBOARD_SHORTCUTS.LIMIT_ORDER_BUY, '/trade'),
        options: { ignoreInputs: false },
      },
      {
        hotkey: 'Shift+P',
        callback: (event) => handleShortcut(event, KEYBOARD_SHORTCUTS.LIMIT_ORDER_SELL, '/trade'),
        options: { ignoreInputs: false },
      },
      {
        hotkey: 'Shift+H',
        callback: (event) =>
          handleShortcut(event, KEYBOARD_SHORTCUTS.TOGGLE_CHART_ORDERS, '/trade', false),
        options: { ignoreInputs: false },
      },
      {
        hotkey: 'Shift+Q',
        callback: (event) =>
          handleShortcut(event, KEYBOARD_SHORTCUTS.TOGGLE_QUICK_TRADE, '/trade', false),
        options: { ignoreInputs: false },
      },
      {
        hotkey: 'Shift+T',
        callback: (event) =>
          handleShortcut(event, KEYBOARD_SHORTCUTS.OPEN_CONDITIONAL_ORDER, '/trade', false),
        options: { ignoreInputs: false },
      },
      {
        hotkey: 'Shift+X',
        callback: (event) =>
          handleShortcut(event, KEYBOARD_SHORTCUTS.TOGGLE_CHART_FULLSCREEN, '/trade', false),
        options: { ignoreInputs: false },
      },
      {
        hotkey: 'Alt+T',
        callback: (event) =>
          handleShortcut(event, KEYBOARD_SHORTCUTS.DRAW_TRENDLINE, '/trade', false),
        options: { ignoreInputs: false },
      },
      {
        hotkey: 'Alt+H',
        callback: (event) =>
          handleShortcut(event, KEYBOARD_SHORTCUTS.DRAW_HORIZONTAL_LINE, '/trade', false),
        options: { ignoreInputs: false },
      },
      {
        hotkey: 'Delete',
        callback: (event) =>
          handleShortcut(event, KEYBOARD_SHORTCUTS.DELETE_SELECTED_DRAWING, '/trade', false),
        options: { ignoreInputs: true },
      },
      {
        hotkey: 'Backspace',
        callback: (event) =>
          handleShortcut(event, KEYBOARD_SHORTCUTS.DELETE_SELECTED_DRAWING, '/trade', false),
        options: { ignoreInputs: true },
      },
    ],
    [handleShortcut],
  )

  useHotkeys(shortcutDefinitions, {
    enabled: hotkeysEnabledForSession,
    preventDefault: false,
    stopPropagation: false,
  })

  return (
    <ShortcutContext.Provider value={{ shortcut, tradeKey, orderBookKey, shortcutKey, reset }}>
      {children}
    </ShortcutContext.Provider>
  )
}

const useShortcuts = () => {
  const context = useContext(ShortcutContext)
  if (context === undefined) {
    throw new Error('useShortcuts must be used within a ShortcutProvider')
  }
  return { ...context, resetShortcuts: context.reset }
}

export { ShortcutProvider, useShortcuts }
